<template>
  <loading v-if="loading" />

  <form
    v-else
    @submit.prevent="submit"
  >
    <p class="has-margin-bottom-150">
      {{
        isLogin
          ? "Welcome back – log in and we'll pick up right where you left off."
          : "Create your free fixed.net account – you'll use this to talk to our team and follow progress on your website cleanup."
      }}
    </p>

    <b-field label="Email">
      <b-input
        ref="email"
        v-model="form.email"
        :disabled="processing"
        type="email"
        autocomplete="email"
        placeholder="Your email"
      />
    </b-field>

    <template v-if="isLogin">
      <b-field
        key="login-password"
        label="Password"
      >
        <b-input
          v-model="form.loginPassword"
          :disabled="processing"
          type="password"
          autocomplete="current-password"
          placeholder="Your password"
          password-reveal
        />
      </b-field>

      <b-field>
        <p>
          <small><a @click="forgotPassword">Forgotten your password?</a></small>
        </p>
      </b-field>
    </template>

    <template v-else>
      <create-password-field
        key="register-password"
        :disabled="processing"
        @validated="form.password = $event"
      />

      <b-field>
        <p class="has-text-grey">
          <small>
            By clicking "Continue" I agree to the fixed.net
            <a
              target="_blank"
              href="https://fixed.net/knowledge-base/article/terms-and-conditions"
            >Terms of Service</a>
          </small>
          .
        </p>
      </b-field>
    </template>

    <b-field>
      <p class="has-text-grey">
        <small v-if="isLogin">
          Need an account?
          <a @click="mode = 'register'">Create one</a>
        </small>
        <small v-else>
          Already have an account?
          <a @click="mode = 'login'">Log in</a>
        </small>
      </p>
    </b-field>

    <step-controls
      :enabled="formIsValid"
      :processing="processing"
      @back="$emit('back')"
    />
  </form>
</template>

<script>
export default {
  name: "GoogleCleanupCreateAccountStep",
  components: {
    "create-password-field": () => import("@shared/account/_createPassword")
  },
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: this.payload.userId ? true : false,
      processing: false,
      advanced: false,
      resumeTimeout: null,
      mode: "register",
      form: {
        email: "",
        password: "",
        loginPassword: ""
      }
    };
  },
  computed: {
    isLogin() {
      return this.mode === "login";
    },
    userId() {
      return this.$store.getters["auth/userId"]();
    },
    formIsValid() {
      if (!this.$v.isEmail(this.form.email)) return false;
      if (this.isLogin) return !!this.form.loginPassword;
      return !!this.form.password;
    },
    formIsDisabled() {
      return this.processing || !this.formIsValid;
    }
  },
  watch: {
    /* Registering OR logging in flips the auth state – either way we pick up
    the userId and continue the flow with that account. */
    userId() {
      this.advance();
    }
  },
  created() {
    this.$nextTick(() => {
      this.$refs.email && this.$refs.email.$el.querySelector("input").focus();
    });
    /* Give an already-signed-in session a moment to rehydrate before showing
    the form – if it does, skip straight past this step. */
    this.resumeTimeout = setTimeout(() => {
      this.loading = false;
      if (this.userId) this.advance();
    }, 660);
  },
  beforeDestroy() {
    clearTimeout(this.resumeTimeout);
  },
  methods: {
    /* Both the auth-state watcher and the resume timeout can reach this, so
    it is guarded to advance the wizard exactly once. */
    advance() {
      if (this.advanced || !this.userId) return;
      this.advanced = true;
      clearTimeout(this.resumeTimeout);
      this.$emit("payload", { userId: this.userId });
      this.$emit("next");
    },
    submit() {
      if (this.formIsDisabled) return;
      this.processing = true;
      const request = this.isLogin
        ? this.$store.dispatch("auth/loginWithPassword", {
            email: this.form.email,
            password: this.form.loginPassword
          })
        : this.$store.dispatch("auth/register", {
            email: this.form.email,
            password: this.form.password
          });
      request.catch(error => {
        this.processing = false;
        this.$toast.open({
          message: (error && error.message) || error,
          position: "is-bottom",
          queue: false,
          type: "is-danger"
        });
      });
    },
    forgotPassword() {
      this.$modal.open({
        parent: this,
        component: () => import("@views/auth/login/_forgottenPasswordModal"),
        canCancel: ["escape", "outside"],
        width: 400,
        props: {
          email: this.form.email
        }
      });
    }
  }
};
</script>
